import { CloseIcon } from '@chakra-ui/icons';
import { Divider, Flex, Text, useToast } from '@chakra-ui/react';
import { AntiVirusStatuses } from 'common';
import MyModal from 'components/MyModal';
import AdditionalData from 'containers/AdditionalData';
import UploadFileInput from 'containers/UploadFileInput';
import usePaymentOpsUploadFile from 'hooks/usePaymentOpsUploadFile';
import { DisbursementDataType, PaymentKey } from 'pages/Payment';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { usePaymentOptsUploadReportFileMutation } from 'redux/services/approvalProcessor';
import { PaymentOpsUploadReportFileRequest } from 'redux/services/approvalProcessor/types';
import { useGetProfileQuery } from 'redux/services/membership';
import { getOrganisationMembership } from 'utils';

interface UploadDisbursementReportModalProps {
    readonly isOpen: boolean;
    readonly setOpenModalCreate: () => void;
    readonly processId: string;
    readonly orgId: string | undefined;
    onCloseComplete?: () => void;
}

const UploadDisbursementReportModal = (
    { isOpen, setOpenModalCreate, onCloseComplete, processId, orgId }: UploadDisbursementReportModalProps
) => {
    const [paymentReportFiles, setPaymentReportFile] = useState(undefined);

    const intervalPrefundRef: React.MutableRefObject<any> = useRef(null);

    const onDropProof = useCallback((acceptedFiles) => {
        setPaymentReportFile(acceptedFiles);
    }, []);

    const {
        getRootProps: getProofRootProps,
        getInputProps: getProofInputProps,
        open: openProofInputProps,
        isDragActive: isProofDragActive,
    } = useDropzone({
        onDrop: onDropProof,
        multiple: false,
        accept: [".xls", ".xlsx", ".csv"],
        disabled: false,
    });

    const toast = useToast();

    const { data: profileData } = useGetProfileQuery();
    const membershipOrganisation = getOrganisationMembership(
        profileData?.data?.memberships
    );

    const [
        paymentOptsUploadReportFile,
        {
            isLoading: isPaymentOptsUploadReportFileLoading,
        },
    ] = usePaymentOptsUploadReportFileMutation();

    const defaultDisbursementData: DisbursementDataType =
    {
        key: PaymentKey.UPLOAD_DISBURSEMENT_REPORT,
        title: "Upload Disbursement Report",
        supportText:
            "Max file size is 5MB. Supported file types are .csv, .xls and .xlsx.",
        validations: {
            maxFileSize: {
                value: 5 * 1024 * 1024,
                message: "File exceeds size limit",
            },
            fileType: {
                value: [".xls", ".xlsx", ".csv"],
                message: "File type is invalid",
            },
        },
        isSuccess: false,
        rootProps: getProofRootProps,
        inputProps: getProofInputProps,
        open: openProofInputProps,
        files: [],
    };

    const {
        isLoadingReport,
        reportFile,
        disbursementData: hookDisbursementData,
        reportDocumentDetail,
        hasErrorReport,
        errorMessagesReport,
        uploadReportFileData,
        isEnableSubmit,
        handleRemoveFile
    } = usePaymentOpsUploadFile({ defaultDisbursementData: [defaultDisbursementData], paymentReportFiles, intervalPrefundRef, orgId });

    const disbursementData = hookDisbursementData[0] ?? defaultDisbursementData;

    const handleSubmit = async () => {
        try {
            const body: PaymentOpsUploadReportFileRequest = {
                orgToken: membershipOrganisation?.token ?? "",
                processId,
                processVariables: {
                    overallInformation: {
                        outputDocumentId: uploadReportFileData?.data?.id ?? "",
                        outputDocumentName: reportFile?.fileName || "",
                    },
                },
            };
            await paymentOptsUploadReportFile(body).unwrap();
            toast({
                title: "File Submitted Successfully",
                description: "",
                status: "success",
                duration: 9000,
                position: "top-right",
                isClosable: true,
            });
            setOpenModalCreate();
            onCloseComplete?.();
        } catch (error: any) {
            const errorMessage = error?.data?.message || "An error occurred while submitting the file.";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 9000,
                position: "top-right",
                isClosable: true,
            });
        }
    };

    const isHasFiles = disbursementData?.files && disbursementData?.files?.length > 0;

    return (
        <MyModal
            isOpen={isOpen}
            onClose={() => setOpenModalCreate()}
            title="Upload Disbursement Report"
            submitBtnText="Submit"
            cancelBtnText="Cancel"
            onSubmit={handleSubmit}
            loadingSubmit={isEnableSubmit && isPaymentOptsUploadReportFileLoading}
            scrollBehavior="inside"
            disabledCloseOutSide
            disableSubmit={!isEnableSubmit}
            closeable={false}
        >
            {!isHasFiles && (
                <UploadFileInput
                    disbursementData={disbursementData}
                    isHasFiles={isHasFiles}
                    isDragActive={isProofDragActive}
                />
            )}

            {disbursementData?.files?.map((file) => (
                <Flex
                    key={file.fileName}
                    flexDir="column"
                    border={
                        file?.errors ||
                            (disbursementData.key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT &&
                                reportDocumentDetail?.data?.antiVirusStatus ===
                                AntiVirusStatuses.INFECTED)
                            ? "1px solid #DA1E28"
                            : ""
                    }
                    mt={"10px"}
                >
                    <Flex
                        bgColor={"#F4F4F4"}
                        height="48px"
                        alignItems="center"
                        justifyContent="space-between"
                        px="16px"
                        data-testid={`${disbursementData.key}-filename-uploaded`}
                    >
                        <Text fontSize={"10px"} noOfLines={1}>
                            {file.fileName}
                        </Text>
                        <CloseIcon
                            data-testid={`${disbursementData.key}-close-icon`}
                            cursor="pointer"
                            onClick={() => {
                                if (isPaymentOptsUploadReportFileLoading) return;

                                handleRemoveFile(
                                    disbursementData.key,
                                    intervalPrefundRef,
                                );
                            }}
                        />
                    </Flex>
                    <Divider
                        h="1px"
                        w="100%"
                        bgColor="#E7E7E7"
                        borderColor={"initial"}
                        display={!file?.errors ? "none" : "block"}
                    />

                    {disbursementData?.key === PaymentKey.UPLOAD_DISBURSEMENT_REPORT &&
                        (!file?.errors || file?.errors?.length === 0) &&
                        <AdditionalData
                            isLoading={isLoadingReport}
                            hasError={hasErrorReport}
                            errorMessages={errorMessagesReport}
                            doc={reportDocumentDetail}
                        />}

                    {file?.errors?.map((error) => (
                        <Flex
                            key={error}
                            bgColor={"#F4F4F4"}
                            alignItems="center"
                            data-testid={`${disbursementData.key}-error-messages`}
                            w="100%"
                        >
                            <Text
                                color={"red"}
                                px="16px"
                                py="8px"
                                whiteSpace="pre-wrap"
                                maxW="100%"
                                wordBreak="break-word"
                            >
                                {error}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            ))}
        </MyModal>
    );
};

export default UploadDisbursementReportModal;
